import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faPlus, faStar } from '@fortawesome/free-solid-svg-icons';
import client from '../../../lib/feathers';

/**
 * Manage catalog-provenance includes for a listing (the "wines provided by"
 * relationship — separate from location/host). A listing displays the union of
 * its own wines + included sources' wines. One include can be the BRAND SOURCE
 * (new wines added here route to it). For Ridge/WVV satellites + wine bars.
 */
export default function CatalogIncludes() {
  const { lid } = useParams();
  const role = useSelector((state) => state.auth.user.role);
  const [includes, setIncludes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const svc = useCallback(() => client.service(`${role}/listing-catalog-include`), [role]);

  const load = useCallback(async () => {
    try {
      const res = await svc().find({ query: { listingId: lid, $limit: 100 } });
      setIncludes(res.data || res || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load included catalogs');
    }
  }, [svc, lid]);

  useEffect(() => { if (role && lid) load(); }, [role, lid, load]);

  const loadListingOptions = async (keywords) => {
    const res = await client.service(`${role}/listing`).find({
      query: { $limit: 25, $sort: { name: 1 }, name: { $iLike: `%${keywords || ''}%` } },
    });
    return (res.data || []).map((l) => ({ ...l, value: l.id, label: l.name }));
  };

  const addInclude = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await svc().create({ listingId: lid, includedListingId: selected.value });
      setSelected(null);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to add include');
    } finally {
      setBusy(false);
    }
  };

  const removeInclude = async (row) => {
    if (!window.confirm(`Stop including the catalog of "${row.includedListing?.name || 'this listing'}"?`)) return;
    setBusy(true);
    try {
      await svc().remove(row.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to remove include');
    } finally {
      setBusy(false);
    }
  };

  // Make `row` the brand source: unset any current brand source first (≤1).
  const setBrandSource = async (row) => {
    setBusy(true);
    try {
      const current = includes.find((i) => i.isBrandSource && i.id !== row.id);
      if (current) await svc().patch(current.id, { isBrandSource: false });
      await svc().patch(row.id, { isBrandSource: true });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to set brand source (designating a brand requires managing the source listing)');
    } finally {
      setBusy(false);
    }
  };

  const clearBrandSource = async (row) => {
    setBusy(true);
    try {
      await svc().patch(row.id, { isBrandSource: false });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to clear brand source');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Included catalogs</h3>
          <p className="mt-1 text-sm text-gray-500">
            Show wines from another listing on this one (this listing keeps its own address).
            Mark one as the <strong>brand source</strong> to make new wines added here belong to it
            (e.g. a Ridge satellite → Ridge). Wine bars can include many without a brand source.
          </p>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Add a listing whose wines should show here</label>
          <form onSubmit={addInclude} className="flex w-full items-center mt-1">
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadListingOptions}
              onChange={setSelected}
              value={selected}
              className="w-full whitespace-pre-wrap"
              placeholder="Search listings…"
            />
            <button type="submit" disabled={busy || !selected}
              className="items-center px-4 py-2 my-1 mx-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-transparent hover:bg-primary hover:text-white disabled:opacity-50">
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </form>

          {error && <div className="mt-2 text-sm text-red-700">{error}</div>}

          <table className="min-w-full divide-y divide-gray-300 mt-4">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Source listing</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Brand source</th>
                <th className="px-3 py-2"><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {includes.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-3 text-sm text-gray-700">
                    {row.includedListing ? (
                      <a href={`/admin/listings/${row.includedListing.id}`} className="text-indigo-700 hover:underline">
                        {row.includedListing.name}
                      </a>
                    ) : <span className="text-gray-400 italic">{row.includedListingId}</span>}
                    {row.status === 'blocked' && <span className="ml-2 text-xs text-red-600">(blocked by source)</span>}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {row.isBrandSource ? (
                      <button type="button" disabled={busy} onClick={() => clearBrandSource(row)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <FontAwesomeIcon icon={faStar} /> brand source (clear)
                      </button>
                    ) : (
                      <button type="button" disabled={busy} onClick={() => setBrandSource(row)}
                        className="text-xs text-indigo-600 hover:underline">
                        set as brand source
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button type="button" disabled={busy} onClick={() => removeInclude(row)}
                      className="items-center px-3 py-1.5 border border-primary rounded-md text-sm font-medium text-primary hover:bg-primary hover:text-white disabled:opacity-50">
                      <FontAwesomeIcon icon={faX} />
                    </button>
                  </td>
                </tr>
              ))}
              {includes.length === 0 && (
                <tr><td colSpan={3} className="p-3 text-sm text-gray-400 italic">No included catalogs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
